/** The host shell: sign-in, socket and toast, and nothing about any game. */

import { useEffect, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useSession } from '../lib/useSession';
import { rememberGame } from '../lib/myGames';
import { uiFor } from '../games';
import { AuthGate } from '../components/AuthGate';
import { Toast } from '../kit/Toast';
import { Interstitial } from '../show/Interstitial';
import { RoomControls } from '../show/RoomControls';
import { ShowBar } from '../show/ShowBar';

export function Host() {
  const { code = '' } = useParams();
  return (
    <AuthGate title={`Host control · ${code}`}>
      <HostInner code={code} />
    </AuthGate>
  );
}

function HostInner({ code }: { code: string }) {
  // The cookie on the socket handshake is the credential.
  const autoJoin = useMemo(() => ({ as: 'host' as const, code }), [code]);
  const { snapshot, connected, joinError, toast, send, submit, notify, events, clearToast } =
    useSession(autoJoin);

  // Anything hosted from this browser shows up under "My games" on the home page.
  const title = snapshot?.core.title;
  const sessionCode = snapshot?.core.code;
  useEffect(() => {
    if (sessionCode) {
      rememberGame({ code: sessionCode, name: title ?? sessionCode, role: 'hosted' });
    }
  }, [sessionCode, title]);

  if (joinError) {
    return (
      <div className="centered">
        <div className="card col">
          <h1>Can’t host {code}</h1>
          <p className="error-text">{joinError}</p>
          <Link className="btn" to="/">
            Back
          </Link>
        </div>
      </div>
    );
  }

  if (!snapshot) {
    return (
      <div className="centered">
        <p className="muted">{connected ? 'Loading game…' : 'Connecting…'}</p>
      </div>
    );
  }

  // A null segment means core owns the screen (interstitial or results). Distinct from
  // "no UI for that game", the fallback below.
  if (!snapshot.core.segment) {
    return (
      <>
        <RoomControls snapshot={snapshot} notify={notify} />
        <Interstitial snapshot={snapshot} send={send} surface="host" connected={connected} />
        <Toast message={toast} onDismiss={clearToast} />
      </>
    );
  }

  const ui = uiFor(snapshot.core.segment.gameId);

  return (
    <>
      <ShowBar snapshot={snapshot} send={send} connected={connected} />
      <RoomControls snapshot={snapshot} notify={notify} />
      {ui ? (
        <ui.host
          snapshot={snapshot}
          send={send}
          submit={submit}
          events={events}
          connected={connected}
          notify={notify}
        />
      ) : (
        <div className="centered">
          <div className="card col">
            <h1>{snapshot.core.title}</h1>
            <p className="muted">
              This build has no host screen for “{snapshot.core.segment.gameId}”.
            </p>
          </div>
        </div>
      )}
      <Toast message={toast} onDismiss={clearToast} />
    </>
  );
}
